import { WORDMARK } from './wordmark.js';

// The Outlevo logo (owner request 2026-10-23, Outlevo-Feature-Specifications/Brand: logo and share images.md):
// the tick-arrow, a check mark whose long stroke leaves as an arrow (checked, then sent on), white on a green
// rounded square, beside "outlevo" in Onest 800 lowercase with tight tracking. One source for the site, the
// portal, the share images, the ad images, the icons (scripts/gen-icons.ts) and the email header.

export const BRAND = {
  green: '#11883f', // the mark's tile, the site's action pill
  ink: '#0b0d12', // the wordmark on light
  paper: '#ffffff', // the wordmark on dark, the glyph
  lavender: '#f4f1ff', paleBlue: '#f3f6fd', blue: '#1769e0', muted: '#4b5262',
} as const;

/** The glyph, in a 32×32 box: one stroked path (width 4, round caps and joins). */
export const MARK_D = 'M7.6 16.6l5.2 5.2L24.2 10.4M16.6 10.4h7.6v7.6';
export const MARK_RADIUS = 9;

/** The mark's elements in its 32×32 box. `tile: null` draws the glyph alone (the one-colour use on a plain ground). */
export function markInner(o: { tile?: string | null; glyph?: string } = {}): string {
  const tile = o.tile === undefined ? BRAND.green : o.tile;
  return `${tile ? `<rect width="32" height="32" rx="${MARK_RADIUS}" fill="${tile}"/>` : ''}<path d="${MARK_D}" stroke="${o.glyph ?? BRAND.paper}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`;
}

/** The mark as a standalone SVG document. */
export const markSvg = (o: { size?: number; tile?: string | null; glyph?: string } = {}) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${o.size ?? 32}" height="${o.size ?? 32}" viewBox="0 0 32 32">${markInner(o)}</svg>`;

/** The wordmark as a path in font units (baseline at y = 0, `WORDMARK.width` wide, the "l" 708 tall). */
export const wordmarkInner = (fill: string) => `<path d="${WORDMARK.d}" fill="${fill}"/>`;

// The lockup's proportions, from the mark's height M: the wordmark at 0.86 M (its "l" stands 0.61 M tall, centred
// on the mark), 0.26 M after the mark.
const SIZE = 0.86; const GAP = 0.26;

/** Where the wordmark sits in the lockup at mark height `m`: its left edge, baseline and scale (font units to px). */
export function lockupGeometry(m: number) {
  return { width: Math.ceil(m * (1 + GAP) + (WORDMARK.width * SIZE * m) / 1000), x: r(m * (1 + GAP)), baseline: r(m / 2 + 0.354 * SIZE * m), scale: (SIZE * m) / 1000 };
}
/** The lockup's width at mark height `m`. */
export const lockupWidth = (m: number) => lockupGeometry(m).width;

/** The lockup (mark + wordmark) at mark height `m`, placed at (x, y) top-left. */
export function lockupInner(x: number, y: number, m: number, o: { ink?: string; tile?: string | null; glyph?: string } = {}): string {
  const g = lockupGeometry(m);
  return `<g transform="translate(${x} ${y})"><g transform="scale(${m / 32})">${markInner(o)}</g><g transform="translate(${g.x} ${g.baseline}) scale(${g.scale.toFixed(5)})">${wordmarkInner(o.ink ?? BRAND.ink)}</g></g>`;
}

/** The lockup as a standalone SVG document, `m` px high. */
export const lockupSvg = (m: number, o: { ink?: string; tile?: string | null; glyph?: string; background?: string } = {}) => {
  const w = lockupWidth(m);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${m}" viewBox="0 0 ${w} ${m}">${o.background ? `<rect width="${w}" height="${m}" fill="${o.background}"/>` : ''}${lockupInner(0, 0, m, o)}</svg>`;
};

/** The email header's lockup, `m` px high on a white rounded chip with `pad` px around it: mail apps in dark mode
 * darken the message but not its images, so a transparent lockup's dark wordmark would vanish. On the white card of
 * a light-mode message the chip doesn't show. */
export const EMAIL_PAD = 8;
export const emailLockupSvg = (m: number, pad = EMAIL_PAD) => {
  const w = lockupWidth(m) + pad * 2;
  const h = m + pad * 2;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="${w}" height="${h}" rx="${pad}" fill="#ffffff"/>${lockupInner(pad, pad, m)}</svg>`;
};

const r = (n: number) => Math.round(n * 100) / 100;
